import CalendarEntryContentView from "Components/Calendar/CalendarEntryContentView";
import CalendarEntryDraggableView from "Components/Calendar/CalendarEntryDraggableView";
import {
	CALENDAR_ENTRY_HEIGHT,
	CALENDAR_ENTRY_TYPE_HEIGHT,
	CalendarEntry,
	TABLE_CELL_MARGIN_Y,
	useDraggedItem,
} from "Components/Calendar/CalendarEntryHelper";
import { CellSize } from "Components/Calendar/CalendarHelper";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { ResizePixelSet } from "Components/Resize/ResizeHelper";
import ResizeView, { ResizeDirection } from "Components/Resize/ResizeView";
import { MinuteRange } from "Components/WorkingHours/types/MinuteRange";

import { useIsBrowserOrIpadOs } from "Helpers/PlatformHelper";
import { ValidData } from "Store/types/ValidData";
import { Project } from "domain-entities";
import moment from "moment";
import React, { CSSProperties, FC, useState } from "react";

interface Props {
	calendarEntry: CalendarEntry;
	originalCalendarEntry?: CalendarEntry;
	colorHex: string;
	projects: Project[];
	scheduleEntryTypeIndex: number;
	cellSize: CellSize;
	hour: number;
	visibleMinutes: MinuteRange;
	resizePixelSet: ResizePixelSet;
	dropType: string;
	isFullDay?: boolean;
	isNew?: boolean;
	isSingleRow?: boolean;
	selectCalendarEntry: () => void;
	onResizeCalendarEntry: (
		calendarEntry: Partial<CalendarEntry> & ValidData
	) => Promise<void>;
	tooltip: JSX.Element | null;
}

const CalendarDayEntryView: FC<Props> = (props) => {
	const isInternalAdmin = useIsInternalAdmin();
	const [isResizing, setIsResizing] = useState(false);
	const [resizeWidth, setResizeWidth] = useState(0);
	const [resizeLeft, setResizeLeft] = useState(0);
	const selectedDate = useCalendar((state) => state.selectedDate);
	const endOfDay = selectedDate.clone().add(1, "day");
	
	// Use original calendar entry for cut-off detection if provided
	const entryForCutOff = props.originalCalendarEntry || props.calendarEntry;
	const startTime = moment.unix(props.calendarEntry.startTime);
	const endTime = moment.unix(props.calendarEntry.endTime);
	const startTimeForCutOff = moment.unix(entryForCutOff.startTime);
	const endTimeForCutOff = moment.unix(entryForCutOff.endTime);
	
	const startTimeMinutes = startTime.hours() * 60 + startTime.minutes();
	const isStartTimeBeforeStartOfDay = startTime.isBefore(selectedDate);
	const isEndTimeAfterEndOfDay = endTime.isAfter(endOfDay);
	
	// Use original times for cut-off detection
	const isStartTimeBeforeStartOfDayForCutOff = startTimeForCutOff.isBefore(selectedDate);
	const isEndTimeAfterEndOfDayForCutOff = endTimeForCutOff.isAfter(endOfDay);
	
	const isBrowserOrIpadOs = useIsBrowserOrIpadOs();
	const { isDragging, drag, preview } = useDraggedItem(
		props.dropType,
		props.calendarEntry,
		props.colorHex,
		props.isNew
	);

	/**
	 * Calculates the visible width of a calendar entry in pixels.
	 * Handles events that:
	 * - Span across days
	 * - Are partially visible
	 * - Start before or end after the visible time window
	 */
	function getWidth() {
		// Convert hour width to pixels per minute for calculations
		const minuteWidth = props.cellSize.day.hourWidth / 60;
		const { start: visibleStart, end: visibleEnd } = props.visibleMinutes;

		if (props.isFullDay) {
			// For full-day entries, calculate width based on visible hours
			const visibleDurationMinutes = visibleEnd - visibleStart;
			// Subtract 1px from full-day entries to make dotted lines more visible
			return `${(visibleDurationMinutes * minuteWidth) - 2}px`;
		}

		// Calculate event start in minutes since midnight
		// If event starts before today, use 00:00 (midnight) as start
		const effectiveStartMinutes = isStartTimeBeforeStartOfDay
			? 0 // midnight
			: startTime.hours() * 60 + startTime.minutes();

		// Calculate event end in minutes since midnight
		// If event ends after today, use 24:00 (next midnight) as end
		const effectiveEndMinutes = isEndTimeAfterEndOfDay
			? 24 * 60 // end of day in minutes
			: endTime.hours() * 60 + endTime.minutes();

		// Clip event to visible time window
		const clippedStartMinutes = Math.max(effectiveStartMinutes, visibleStart);
		const clippedEndMinutes = Math.min(effectiveEndMinutes, visibleEnd);

		// Calculate final width in pixels based on visible duration
		const durationMinutes = Math.max(0, clippedEndMinutes - clippedStartMinutes);
		return `${durationMinutes * minuteWidth}px`;
	}

	function getLeft() {
		if (props.isFullDay) {
			return '0';
		}

		if (isStartTimeBeforeStartOfDay) {
			return '0';
		}

		const hourWidth = props.cellSize.day.hourWidth;
		const minuteWidth = hourWidth / 60;
		const minutesOffset = startTimeMinutes % 60;
		const pixelOffset = minutesOffset * minuteWidth;
		return `${pixelOffset}px`;
	}

	function getStyle() {
		const y =
			TABLE_CELL_MARGIN_Y +
			(props.scheduleEntryTypeIndex >= 0 && !props.isSingleRow
				? props.scheduleEntryTypeIndex * CALENDAR_ENTRY_TYPE_HEIGHT +
				  CALENDAR_ENTRY_HEIGHT
				: 0);

		const baseLeft = parseFloat(getLeft());
		const baseWidth = parseFloat(getWidth());

		const style: CSSProperties = {
			position: "absolute",
			left: `${baseLeft + resizeLeft}px`,
			width: `${baseWidth + resizeWidth}px`,
			transform: `translate3d(0, ${y}px, 0)`,
			zIndex: isResizing ? 2 : 1,
		};

		return style;
	}

	function renderResizeView(resizeDirection: ResizeDirection) {
		if (
			isInternalAdmin &&
			isBrowserOrIpadOs &&
			((!isStartTimeBeforeStartOfDay && resizeDirection === "left") ||
				(!isEndTimeAfterEndOfDay && resizeDirection === "right"))
		) {
			const hourWidth = props.cellSize.day.hourWidth;
			const hoursInDay = Math.ceil(props.visibleMinutes.end / 60) - Math.floor(props.visibleMinutes.start / 60);
			const columnWidth = hourWidth * hoursInDay;

			return (
				<ResizeView
					columnWidth={columnWidth}
					calendarEntry={props.calendarEntry}
					calendarEntryWidth={parseFloat(getWidth())}
					calendarEntryLeft={parseFloat(getLeft())}
					resizeDirection={resizeDirection}
					resizePixels={props.resizePixelSet[resizeDirection]}
					isResizing={isResizing}
					isTooltipVisible={!props.isFullDay}
					isFullDay={props.isFullDay}
					setIsResizing={setIsResizing}
					setResizeWidth={setResizeWidth}
					setResizeLeft={setResizeLeft}
					selectScheduleEntry={props.selectCalendarEntry}
					onChange={(dateTime) =>
						props.onResizeCalendarEntry({
							id: props.calendarEntry.id,
							[resizeDirection === "left" ? "startTime" : "endTime"]: dateTime,
						})
					}
				/>
			);
		}
	}

	return (
		<CalendarEntryDraggableView
			calendarEntry={props.calendarEntry}
			colorHex={props.colorHex}
			isDragging={isDragging}
			isResizing={isResizing}
			onClick={props.selectCalendarEntry}
			preview={preview}
			style={getStyle()}
			isSingleRow={props.isSingleRow}
			isCutOff={{
				left: isStartTimeBeforeStartOfDayForCutOff,
				right: isEndTimeAfterEndOfDayForCutOff,
			}}
			tooltip={props.tooltip}
		>
			{renderResizeView("left")}
			<CalendarEntryContentView
				isDragging={isDragging}
				calendarEntry={props.calendarEntry}
				projects={props.projects}
				drag={drag}
			/>
			{renderResizeView("right")}
		</CalendarEntryDraggableView>
	);
};

export default CalendarDayEntryView;
